import { useState, useCallback, useRef } from "react";
import { ServiceCase, AdvancedSearchServiceCaseRequest, useAdvancedSearchServiceCases } from "@coherehealth/qm-api";
import { useSnackbar } from "notistack";

export function useSearchResults() {
  const { mutate: advancedSearch, loading } = useAdvancedSearchServiceCases({});
  const [results, setResults] = useState<ServiceCase[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const searchSeqRef = useRef(0);

  const search = useCallback(
    async (request: AdvancedSearchServiceCaseRequest) => {
      const seq = ++searchSeqRef.current;
      try {
        const response = await advancedSearch(request);
        if (seq !== searchSeqRef.current) {
          return;
        }
        setResults(response.results ?? []);
        setTotalCount(response.countResponse?.count ?? 0);
      } catch {
        if (seq === searchSeqRef.current) {
          enqueueSnackbar("Search failed. Please try again.", {
            variant: "error",
            preventDuplicate: true,
          });
        }
      }
    },
    [advancedSearch, enqueueSnackbar]
  );

  const clearResults = useCallback(() => {
    ++searchSeqRef.current; // invalidate any in-flight search so its response doesn't overwrite the cleared state
    setResults([]);
    setTotalCount(0);
  }, []);

  const updateResult = useCallback((updated: ServiceCase) => {
    setResults((prev) => prev.map((sc) => (sc.id === updated.id ? updated : sc)));
  }, []);

  return { results, totalCount, loading, search, clearResults, updateResult };
}
